/**
 * What the global interceptor does with a failure, once the classifier has
 * decided what to say about it. Kept separate from User.jsx so it can be
 * tested without mounting the whole provider tree — it governs every flow in
 * the app, so it should not be the one part nothing covers.
 *
 *   notify  — show the user a notification
 *   swallow — resolve instead of rejecting, so callers need no catch
 */

const MUTATIONS = ["post", "put", "patch", "delete"];

export function interceptorPolicy({ status, method, quiet = false }) {
  // `metadata: { quiet: true }` on the request: a non-blocking panel that
  // shows its own inline state. Record the failure, never toast it, and hand
  // it back so the panel can tell "unavailable" apart from a real error.
  if (quiet) {
    return { notify: false, swallow: false };
  }

  // No response at all: a dropped connection or a blocked request.
  if (!status) {
    // A background GET the user never asked for is recorded but not announced
    // — they cannot act on it, and one dropped connection would otherwise fire
    // a notification for every in-flight request. A mutation is announced:
    // they pressed something and need to know it did not land.
    return {
      notify: MUTATIONS.includes(String(method ?? "").toLowerCase()),
      swallow: true,
    };
  }

  // Server faults are swallowed: the user has been told, and there is nothing
  // a caller's catch block can usefully add.
  if (status >= 500) {
    return { notify: true, swallow: true };
  }

  // Client faults keep rejecting, so form-level handling still runs — 422
  // field highlighting, 404 routing, 403 redirects.
  return { notify: true, swallow: false };
}
